import React, { useState, useEffect } from 'react';
import { Modal, Select, message } from 'antd';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { CloseOutlined } from '@ant-design/icons';
import { ButtonWrapper } from 'components/ButtonWrapper';
import { getClientList } from 'store/clientSlice';
import { addUser } from 'store/pageSlice';

const StyledModal = styled(Modal)`
	.ant-modal-content {
		padding: 26px 32px 28px 32px;
		background: #fff;
		border-radius: 8px;
		width: 628px !important;
		min-width: 628px;
		max-width: 90vw;
	}
	.ant-modal-header {
		border-bottom: none;
		padding: 0;
		background: #fff;
	}
	.ant-modal-title {
		font-family: 'Inter', sans-serif;
		font-weight: 600;
		font-size: 24px;
		color: #000;
		line-height: 29px;
		padding-bottom: 0;
	}
	.ant-modal-body {
		padding: 0;
		background: #fff;
	}
	.ant-modal-footer {
		display: none;
	}
`;
const ModalSection = styled.div`
	display: flex;
	flex-direction: column;
	gap: 48px;
	width: 564px;
	margin-top: 12px;
`;
const ModalField = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	width: 566px;
`;
const ModalLabel = styled.div`
	font-family: 'Inter', sans-serif;
	font-weight: 400;
	font-size: 16px;
	color: #0c0c0c;
`;
const ModalDropdown = styled(Select)`
	width: 566px !important;
	height: 40px !important;
	border: 1px solid #c6c6c8 !important;
	border-radius: 2px !important;
	.ant-select-selector {
		border: none !important;
		height: 40px !important;
		font-size: 12px;
		color: #8a8989;
		align-items: center;
		display: flex;
	}
`;
const ModalFooter = styled.div`
	display: flex;
	flex-direction: row;
	gap: 27px;
	width: 100%;
	margin-top: 32px;
	justify-content: center;
`;

export default function AddTeamMemberModal({ open, onCancel, teamRows, buId }) {
	const [role, setRole] = useState(undefined);
	const [loading, setLoading] = useState(false);
	const [userOptions, setUserOptions] = useState([]);
	const [selectedUser, setSelectedUser] = useState(undefined);
	const dispatch = useDispatch();
	const cityManagerExists = teamRows?.some((row) => row.role === 'City-Manager');
	const isCityManagerSelected = role === 'city_manager';
	const disableAdd = isCityManagerSelected && cityManagerExists;

	useEffect(() => {
		const search_type = 'employee';
		let filter_by = {};
		if (role === 'city_manager') filter_by = { user_type: 'city_manager' };
		else if (role === 'mis_executive') filter_by = { user_type: 'mis_executive' };
		else if (role === 'supervisor') filter_by = { user_type: 'supervisor' };
		setLoading(true);
		dispatch(getClientList({ search_type, filter_by, limit: 100 }))
			.then((res) => {
				setSelectedUser(res.id);
				const options = (res?.rows || res?.data || []).map((item) => ({
					value: item.id,
					label:
						`${item.first_name || ''} ${item.last_name || ''}`.trim() ||
						item.username ||
						'',
				}));
				setUserOptions(options);
			})
			.finally(() => setLoading(false));
	}, [role, dispatch]);

	const handleAdd = async () => {
		if (!selectedUser || !role) {
			message.error('Please select a user and a role.');
			return;
		}
		setLoading(true);
		try {
			let displayRole = '';
			if (role === 'city_manager') displayRole = 'City Manager';
			else if (role === 'mis_executive') displayRole = 'MIS Executive';
			else if (role === 'supervisor') displayRole = 'Supervisor';
			await dispatch(
				addUser({
					data: {
						update_type: 'assign_bu_user',
						data: {
							bu_id: buId,
							user_id: selectedUser,
							role: displayRole,
						},
					},
					isEditMode: true,
				})
			);
			message.success('User assigned to Business Unit successfully');
			setRole(undefined);
			setSelectedUser(undefined);
			window.location.reload();
		} catch (error) {
			console.error('Failed to assign user to Business Unit.');
		} finally {
			setLoading(false);
		}
	};

	AddTeamMemberModal.propTypes = {
		open: PropTypes.bool.isRequired,
		onCancel: PropTypes.func.isRequired,
		teamRows: PropTypes.array.isRequired,
		buId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	};

	return (
		<StyledModal
			open={open}
			onCancel={onCancel}
			title="Add Team Member"
			centered
			footer={null}
			closeIcon={
				<CloseOutlined
					onClick={() => {
						setRole(undefined);
						onCancel();
					}}
					style={{ fontSize: 20, color: '#000' }}
				/>
			}
		>
			<ModalSection>
				<ModalField>
					<ModalLabel>Role *</ModalLabel>
					<ModalDropdown
						placeholder="Select Role"
						value={role}
						onChange={setRole}
						options={[
							{ value: 'city_manager', label: 'City Manager' },
							{ value: 'mis_executive', label: 'Mis Executive' },
							{ value: 'supervisor', label: 'Supervisor' },
						]}
						style={{ background: '#fff' }}
					/>
				</ModalField>
				<ModalField>
					<ModalLabel>Name *</ModalLabel>
					<ModalDropdown
						placeholder="Select Name"
						value={selectedUser}
						onChange={setSelectedUser}
						options={userOptions}
						loading={loading}
						showSearch
						filterOption={(input, option) =>
							option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
						}
						disabled={!role}
					/>
				</ModalField>
				{disableAdd && (
					<div style={{ color: '#FF3B30', fontSize: 14, marginTop: -32 }}>
						A City Manager already exists for this Business Unit.
					</div>
				)}
				<ModalFooter>
					<ButtonWrapper
						onClick={() => {
							setRole(undefined);
							onCancel();
						}}
						style={{
							border: '1px solid #FF3B30',
							color: '#FF3B30',
							background: '#fff',
							borderRadius: 4,
							height: 40,
							minWidth: 135.5,
							fontSize: 16,
							fontWeight: 500,
						}}
						disabled={loading}
					>
						Cancel
					</ButtonWrapper>
					<ButtonWrapper
						type="primary"
						style={{
							background: '#143E60',
							color: '#fff',
							border: 'none',
							borderRadius: 4,
							height: 40,
							minWidth: 135.5,
							fontSize: 16,
							fontWeight: 500,
						}}
						disabled={disableAdd || !selectedUser || !role || loading}
						onClick={handleAdd}
						loading={loading}
					>
						Add
					</ButtonWrapper>
				</ModalFooter>
			</ModalSection>
		</StyledModal>
	);
}
